import React, { useEffect, useRef, useState } from 'react';
import { FileText, Upload, ExternalLink } from 'lucide-react';
import Modal from './Modal';
import { apiFetch, ApiError } from '../../lib/api';

const MAX_PDF_BYTES = 10 * 1024 * 1024;
const PDF_MIME = 'application/pdf';

const inputClasses =
  'w-full px-4 py-2.5 rounded-lg bg-white/[0.03] border border-white/[0.06] text-white text-sm placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-[#B0FC38]/30 focus:border-[#B0FC38]/30 focus:bg-white/[0.05] transition-all';
const labelClasses = 'block text-[11px] font-medium text-gray-400/80 mb-1.5 tracking-wide';

function formatBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export default function UploadAuditModal({ open, onClose, onSaved, account }) {
  const [file, setFile] = useState(null);
  const [score, setScore] = useState('');
  const [fieldError, setFieldError] = useState({});
  const [generalError, setGeneralError] = useState('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setFile(null);
    setScore('');
    setFieldError({});
    setGeneralError('');
    setSaving(false);
  }, [open]);

  const onPickFile = (picked) => {
    setFieldError((prev) => ({ ...prev, file: undefined }));
    setGeneralError('');
    if (!picked) {
      setFile(null);
      return;
    }
    if (picked.type !== PDF_MIME) {
      setFieldError({ file: 'Audit file must be a PDF.' });
      setFile(null);
      return;
    }
    if (picked.size > MAX_PDF_BYTES) {
      setFieldError({ file: 'PDF exceeds the 10 MB limit.' });
      setFile(null);
      return;
    }
    setFile(picked);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (saving || !account?.id) return;
    if (!file) {
      setFieldError({ file: 'Pick a PDF to upload.' });
      return;
    }

    if (score !== '') {
      const n = Number(score);
      if (!Number.isInteger(n) || n < 0 || n > 100) {
        setFieldError({ score: 'Score must be an integer 0–100.' });
        return;
      }
    }

    const fd = new FormData();
    fd.append('file', file);
    if (score !== '') fd.append('score', score);

    setSaving(true);
    setFieldError({});
    setGeneralError('');
    try {
      const result = await apiFetch(
        `/api/admin/accounts/${account.id}/audit`,
        { method: 'POST', body: fd }
      );
      onSaved?.(result.account);
      onClose?.();
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.data?.field) {
          setFieldError({ [err.data.field]: err.message });
        } else {
          setGeneralError(err.message || 'Upload failed');
        }
      } else {
        setGeneralError('Network error, please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={saving ? () => {} : onClose}
      title={account ? `Upload audit · ${account.name}` : 'Upload audit'}
      maxWidth="max-w-xl"
      footer={
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2.5 rounded-full border border-white/[0.1] text-sm text-gray-300 hover:text-white hover:border-white/[0.2] transition-all disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="upload-audit-form"
            disabled={saving || !file}
            className="inline-flex items-center justify-center px-5 py-2.5 rounded-full bg-[#B0FC38] text-gray-900 text-sm font-semibold hover:bg-[#9BE22E] shadow-lg shadow-[#B0FC38]/20 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {saving ? (
              <span className="inline-flex items-center gap-2">
                <span className="w-3.5 h-3.5 border-2 border-gray-900 border-t-transparent rounded-full animate-spin" />
                Uploading…
              </span>
            ) : (
              'Save audit'
            )}
          </button>
        </div>
      }
    >
      <form id="upload-audit-form" onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        {generalError && (
          <div className="px-3 py-2 rounded-lg bg-red-500/[0.08] border border-red-500/20 text-[13px] text-red-300/90">
            {generalError}
          </div>
        )}

        {account?.auditPdfUrl && (
          <div className="px-3 py-2.5 rounded-lg bg-white/[0.02] border border-white/[0.06] text-[12px] text-gray-400 flex items-center justify-between gap-3">
            <span>
              Current audit: score <span className="text-gray-200 font-mono">{account.auditScore ?? '—'}</span>
            </span>
            <a
              href={account.auditPdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-gray-300 hover:text-white transition-colors"
            >
              <FileText className="w-3 h-3" />
              View existing
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        )}

        <div>
          <label className={labelClasses}>Audit PDF *</label>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={saving}
            className="w-full px-4 py-6 rounded-lg border border-dashed border-white/[0.12] bg-white/[0.02] text-sm text-gray-400 hover:text-white hover:border-white/[0.25] transition-colors flex items-center justify-center gap-2"
          >
            <Upload className="w-4 h-4" />
            {file ? (
              <span>
                <span className="text-white">{file.name}</span>
                <span className="text-gray-600 ml-2">{formatBytes(file.size)}</span>
              </span>
            ) : (
              <span>Choose a PDF · up to 10 MB</span>
            )}
          </button>
          <input
            ref={inputRef}
            type="file"
            accept="application/pdf,.pdf"
            className="hidden"
            onChange={(e) => onPickFile(e.target.files?.[0] || null)}
          />
          {fieldError.file && <p className="mt-1 text-[11px] text-red-400">{fieldError.file}</p>}
        </div>

        <div>
          <label className={labelClasses}>WCAG score (optional)</label>
          <input
            type="number"
            min={0}
            max={100}
            value={score}
            onChange={(e) => {
              setScore(e.target.value);
              setFieldError((prev) => ({ ...prev, score: undefined }));
              setGeneralError('');
            }}
            className={inputClasses}
            placeholder="0–100"
          />
          <p className="mt-1 text-[11px] text-gray-600">
            Leave blank to keep the current score. 0–100 integer otherwise.
          </p>
          {fieldError.score && <p className="mt-1 text-[11px] text-red-400">{fieldError.score}</p>}
        </div>
      </form>
    </Modal>
  );
}
